<template>
  <div
    class="card"
    :class="[
      marginBottom,
      {
        'is-collapsed': !isExpanded,
        'has-fda-warning': hasWarning,
        'is-active': active,
        'is-nested': nested,
      },
    ]"
  >
    <header class="card-header">
      <span v-if="dragEnabled" class="card-header-icon py-2 pl-3 pr-0">
        <span class="icon has-text-info drag-handle" style="cursor: grab">
          <i class="fas fa-bars"></i>
        </span>
      </span>
      <div
        class="card-header-title py-2 is-flex-grow-1 is-flex is-align-items-center"
        style="width: 100%"
      >
        <!-- Clickable Title Area -->
        <span
          class="is-flex-grow-1 card-toggle-button"
          tabindex="0"
          role="button"
          :aria-expanded="isExpanded.toString()"
          @click="toggleExpanded"
          @keydown.enter="toggleExpanded"
          @keydown.space.prevent="toggleExpanded"
        >
          <slot name="header-title">
            {{ title }}
          </slot>
        </span>

        <!-- Interactive Subtitle Area (Inputs) - Sibling to button -->
        <span
          v-if="!isExpanded && $slots['header-subtitle']"
          class="ml-auto is-flex is-align-items-center pl-3"
          @click.stop
        >
          <slot name="header-subtitle"></slot>
        </span>
        <span v-else-if="!isExpanded && subtitle" class="has-text-grey has-text-weight-normal ml-2">
          {{ subtitle }}
        </span>
      </div>
      <span
        v-if="$slots['header-actions']"
        class="header-actions is-flex is-align-items-center"
        @click.stop
      >
        <slot name="header-actions"></slot>
      </span>
      <span class="card-header-icon py-2" style="cursor: pointer" @click="toggleExpanded">
        <i class="fas fa-angle-down chevron-icon" aria-hidden="true"></i>
      </span>
    </header>

    <SlideTransition>
      <div v-if="isExpanded" class="card-content py-3 px-3">
        <slot></slot>
      </div>
    </SlideTransition>
  </div>
</template>

<script setup>
  import { ref, computed } from "vue";
  import SlideTransition from "./slide-transition.vue";

  const props = defineProps({
    title: { type: String, required: true },
    subtitle: { type: String, default: "" },
    itemLabel: { type: String, required: true },
    index: { type: Number, required: true },
    totalItems: { type: Number, required: true },
    startExpanded: { type: Boolean, default: false },
    expanded: { type: Boolean, default: undefined },
    marginBottom: { type: String, default: "mb-2" },
    dragEnabled: { type: Boolean, default: false },
    hasWarning: { type: Boolean, default: false },
    active: { type: Boolean, default: false },
    nested: { type: Boolean, default: false },
  });

  const emit = defineEmits(["update:expanded", "toggle"]);

  const internalExpanded = ref(props.startExpanded);

  const isExpanded = computed({
    get() {
      return props.expanded !== undefined ? props.expanded : internalExpanded.value;
    },
    set(value) {
      internalExpanded.value = value;
      emit("update:expanded", value);
    },
  });

  function toggleExpanded() {
    isExpanded.value = !isExpanded.value;
    emit("toggle");
  }
</script>

<style lang="scss" scoped>
  @import "~/label/css/labels/supplements/supplement_editor";
  @import "~/label/css/labels/supplements/supplement_mixins";

  .card {
    border: 1px solid $border-standard;
    border-radius: 8px;
    background: white;
    transition:
      box-shadow 0.15s ease,
      border-color 0.15s ease;

    @include card-shadow;

    &.is-active {
      border-color: rgba($focus-outline-color, 0.4);
      box-shadow: 0 2px 8px rgba($focus-outline-color, 0.15);
    }
  }

  .card-header {
    box-shadow: none;
    border-bottom: 1px solid $border-standard;
    user-select: none;
    min-height: 44px;
    transition:
      background-color 0.15s ease,
      border-color 0.15s ease;

    .card.is-active & {
      border-bottom-color: rgba($focus-outline-color, 0.4);
      background-color: rgba($focus-outline-color, 0.03);
    }
  }

  .card-header-title {
    font-weight: 700;
  }

  .card-toggle-button {
    cursor: pointer;
    border-radius: 4px;
    padding: 2px 4px;
    margin: -2px -4px;

    &:focus {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid $focus-outline-color;
      outline-offset: 2px;
    }
  }

  @include chevron-rotation(".card", ".card-header");

  .header-actions {
    padding-right: 0.75rem;
  }

  // Nested/compact variant for constituent cards inside blends
  .card.is-nested {
    border-radius: 6px;
    font-size: 0.875rem;
    border: 1px solid #ccc !important;
    background: white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

    .card-header {
      min-height: 36px;
      max-height: 36px;
      padding: 0;
      border-bottom: 1px solid #ddd !important;
      background-color: #fafafa !important;
    }

    .card-header-title {
      padding: 0.375rem 0.5rem;
      font-size: 0.875rem;
      font-weight: 600;
    }

    .card-header-icon {
      padding: 0.375rem 0.5rem;

      .chevron-icon {
        font-size: 0.875rem;
      }
    }

    .card-content {
      padding: 0.5rem 0.75rem;
      font-size: 0.875rem;
    }
  }
</style>
